import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame } from "lucide-react";

export default function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      nav("/app/products", { replace: true });
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-4">
      <div className="w-full max-w-sm ap-panel p-6" data-testid="register-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-9 w-9 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Flame className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Create account</h1>
            <div className="text-[11px] mono text-zinc-500">AffiliatePilot · phase 1</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="name" className="text-zinc-300">Name (optional)</Label>
            <Input
              id="name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-zinc-300">Email</Label>
            <Input
              id="email" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-zinc-300">Password</Label>
            <Input
              id="password" type="password" required minLength={8} autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100"
              data-testid="register-password-input"
            />
            <div className="text-[10px] mono text-zinc-500 mt-1">min 8 chars</div>
          </div>
          {error && (
            <div className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900/60 rounded-md p-2" data-testid="register-error">
              {error}
            </div>
          )}
          <Button
            type="submit" disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>
        <div className="mt-4 text-xs text-zinc-500 text-center">
          Have an account?{" "}
          <Link to="/login" className="text-emerald-400 hover:underline" data-testid="login-link">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
